import React, { useState } from 'react'
import clsx from 'clsx'
import { PencilIcon, TrashIcon, ClockIcon } from '@heroicons/react/24/outline'
import { Button, Text } from '../../ui'
import { SocialComment, SOCIAL_COMMENT_MAX_LENGTH } from '../../../types/social'
import AuthorAvatar from './AuthorAvatar'
import EditHistoryModal from './EditHistoryModal'
import MentionText from './MentionText'
import MentionTextarea from './MentionTextarea'
import { commentBubbleClass, formatSocialDate } from './socialUtils'
import { formatAuthorName } from '../../../utils/userDisplayName'

export interface CommentListProps {
  comments: SocialComment[]
  currentUserId?: string
  currentUserName?: string | null
  currentUserAvatar?: string | null
  moderation?: boolean
  expandedComments?: boolean
  onAdd: (content: string) => void | Promise<void>
  onUpdate: (commentId: string, content: string) => void | Promise<void>
  onDelete: (commentId: string) => void | Promise<void>
}

const CommentList: React.FC<CommentListProps> = ({
  comments,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  moderation = false,
  expandedComments = false,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [historyComment, setHistoryComment] = useState<SocialComment | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleAdd = async () => {
    const trimmed = draft.trim()
    if (!trimmed) return
    try {
      setSubmitting(true)
      setActionError(null)
      await onAdd(trimmed)
      setDraft('')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao comentar')
    } finally {
      setSubmitting(false)
    }
  }

  const startEdit = (comment: SocialComment) => {
    setEditingId(comment.id)
    setEditDraft(comment.content)
    setActionError(null)
  }

  const saveEdit = async (commentId: string) => {
    const trimmed = editDraft.trim()
    if (!trimmed) return
    try {
      setSubmitting(true)
      setActionError(null)
      await onUpdate(commentId, trimmed)
      setEditingId(null)
      setEditDraft('')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao editar comentário')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Excluir este comentário?')) return
    try {
      setActionError(null)
      await onDelete(commentId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao excluir comentário')
    }
  }

  return (
    <div className={clsx('space-y-3', expandedComments && 'flex flex-col flex-1 min-h-0')}>
      {comments.length > 0 && (
        <div
          className={clsx(
            'space-y-2.5 overflow-y-auto pr-1',
            expandedComments ? 'flex-1 min-h-0 px-5 sm:px-6' : 'max-h-72'
          )}
        >
          {comments.map((comment) => {
            const isOwner = comment.author_id === currentUserId
            const canEdit = isOwner
            const canDelete = isOwner || moderation
            const isEditing = editingId === comment.id

            const ownBubbleClass =
              'rounded-2xl bg-primary-100 dark:bg-primary-900/40 border border-primary-200 dark:border-primary-700/50 px-3 py-2'

            return (
              <div
                key={comment.id}
                className={clsx('flex gap-2 items-start', isOwner && 'flex-row-reverse')}
              >
                <AuthorAvatar
                  name={comment.author?.name}
                  avatarPath={comment.author?.avatar_url}
                  size="sm"
                />
                <div className={clsx('flex-1 min-w-0', isOwner && 'flex flex-col items-end')}>
                  {isEditing ? (
                    <div className="space-y-2 w-full">
                      <MentionTextarea
                        value={editDraft}
                        onChange={setEditDraft}
                        maxLength={SOCIAL_COMMENT_MAX_LENGTH}
                        rows={2}
                        disabled={submitting}
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="solid"
                          disabled={submitting || !editDraft.trim()}
                          onClick={() => saveEdit(comment.id)}
                        >
                          Salvar
                        </Button>
                        <Button variant="outline" onClick={() => setEditingId(null)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={clsx(isOwner ? ownBubbleClass : commentBubbleClass, 'inline-block max-w-full')}>
                        {!isOwner && (
                          <Text size="xs" className="font-semibold text-gray-900 dark:text-gray-100">
                            {formatAuthorName(comment.author)}
                          </Text>
                        )}
                        <p className={clsx('text-sm', isOwner ? 'text-primary-900 dark:text-primary-100' : 'mt-0.5 text-gray-800 dark:text-gray-200')}>
                          <MentionText content={comment.content} />
                        </p>
                      </div>
                      <div className={clsx('flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1', isOwner ? 'mr-1 justify-end' : 'ml-1')}>
                        <Text size="xs" color="muted">{formatSocialDate(comment.created_at)}</Text>
                        {comment.edited_at && (
                          <button
                            type="button"
                            onClick={() => setHistoryComment(comment)}
                            className="text-xs text-gray-500 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            Editado
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => startEdit(comment)}
                            className="text-xs font-medium text-gray-500 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            Editar
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => handleDelete(comment.id)}
                            className="text-xs font-medium text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div
        className={clsx(
          'flex gap-2 items-start pt-1',
          expandedComments && 'shrink-0 px-5 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50/30 dark:bg-gray-900/10'
        )}
      >
        <AuthorAvatar
          name={currentUserName}
          avatarPath={currentUserAvatar}
          size="sm"
        />
        <div className="flex-1 min-w-0 flex gap-2">
          <div className="flex-1 min-w-0">
            <MentionTextarea
              value={draft}
              onChange={setDraft}
              maxLength={SOCIAL_COMMENT_MAX_LENGTH}
              multiline={false}
              placeholder="Escreva um comentário..."
              disabled={submitting}
              onSubmit={handleAdd}
              className="rounded-full !py-2"
            />
          </div>
          <Button
            variant="solid"
            disabled={submitting || !draft.trim()}
            onClick={handleAdd}
            className="self-center shrink-0 !rounded-full !px-4"
          >
            Enviar
          </Button>
        </div>
      </div>

      {actionError && (
        <Text
          size="xs"
          className={clsx(
            'text-red-600 dark:text-red-400',
            expandedComments ? 'shrink-0 px-5 sm:px-6 pb-2' : undefined
          )}
        >
          {actionError}
        </Text>
      )}

      {historyComment && (
        <EditHistoryModal
          title="Histórico do comentário"
          commentId={historyComment.id}
          currentContent={historyComment.content}
          onClose={() => setHistoryComment(null)}
        />
      )}
    </div>
  )
}

export default CommentList
